import { ref } from 'vue';
import { defineStore } from 'pinia';

import { getWfTodoPage } from '@/api/wf';
import { imWsClient } from '@/utils/im/wsClient';

/**
 * 审批中心 Store（HeaderBar「待我审批 (n)」角标 + 审批中心页联动）
 *
 * 关键约定：
 * 1. 角标是**服务端口径**：直接读 `/wf/getTodoPage.json` 的 `totalCount`（taskStatus = 1 待处理），
 *    前端不做本地加减；
 * 2. 实时刷新复用**唯一长连接** {@link imWsClient}（不新建第二条 WS 通道）：收到任意投递帧后
 *    debounce 重新拉取待办数，审批结果 / 新待办都能及时反映；
 * 3. 审批动作（通过/驳回/转交/撤回）完成后由页面显式调用 {@link refreshPendingCount} 对齐。
 */
export const useWfStore = defineStore('wf', () => {
    // ======================== State ========================
    /** 我的待办（task_status = 1 待处理）总数 */
    const pendingCount = ref(0);
    const loading = ref(false);

    // ======================== 订阅句柄 ========================
    let frameOff: (() => void) | null = null;
    let refreshTimer: number | null = null;

    // ======================== Actions ========================

    /**
     * 拉取待办总数（角标唯一数据源）
     */
    async function refreshPendingCount() {
        loading.value = true;
        try {
            const { result } = await getWfTodoPage({ taskStatus: 1, pageNo: 1, pageSize: 1 });
            pendingCount.value = result?.totalCount || 0;
        } catch (e) {
            // 拉取失败保持上一次值（不本地推断，避免角标与后端不一致）
        } finally {
            loading.value = false;
        }
    }

    /**
     * 收到 WS 投递帧：debounce 重新拉取（审批待办 / 结果通知都会触发）
     */
    function scheduleRefresh() {
        if (refreshTimer !== null) {
            return;
        }
        refreshTimer = window.setTimeout(() => {
            refreshTimer = null;
            refreshPendingCount();
        }, 300);
    }

    /**
     * 启动：首次拉取 + 订阅长连接帧（幂等，Layout 挂载时调用）
     */
    async function start() {
        await refreshPendingCount();
        if (!frameOff) {
            frameOff = imWsClient.onFrame(scheduleRefresh);
        }
    }

    /**
     * 停止：解除订阅（退出登录时调用）
     */
    function stop() {
        if (frameOff) {
            frameOff();
            frameOff = null;
        }
        if (refreshTimer !== null) {
            window.clearTimeout(refreshTimer);
            refreshTimer = null;
        }
    }

    /**
     * 清空本地缓存（退出登录 / 切换商家时调用）
     */
    function reset() {
        pendingCount.value = 0;
    }

    return {
        // state
        pendingCount,
        loading,
        // actions
        refreshPendingCount,
        start,
        stop,
        reset
    };
});